/**
 * useChatEvents — Unified event listener for all OS events that feed into chat.
 * Skill results, task alerts, research findings, social mentions, FlzHub messages.
 * Rule 94: Modüler — tek sorumluluk, event-driven.
 */
import { useEffect } from 'react'
import type { ChatMessage } from '../../../contexts/ChatContext'

type AddMessage = (msg: ChatMessage) => void

function ts(): string {
  return new Date().toLocaleTimeString()
}

/** Skill result events → chat message */
function useSkillResultListener(addMessage: AddMessage) {
  useEffect(() => {
    const handler = (e: Event) => {
      const { skillName, result, status, duration } = (e as CustomEvent).detail as {
        skillName: string; result: string; status: string; duration?: number
      }
      const text = status === 'failed'
        ? `❌ ${skillName} başarısız: ${result}`
        : `⚡ ${skillName} tamamlandı${duration ? ` (${duration}s)` : ''}\n${result}`
      addMessage({ role: 'assistant', content: text, ts: ts(), agentName: 'Skill Engine' })
    }
    window.addEventListener('runai:skill-result', handler)
    return () => window.removeEventListener('runai:skill-result', handler)
  }, [addMessage])
}

/** Task alert events → chat message */
function useTaskAlertListener(addMessage: AddMessage) {
  useEffect(() => {
    const handler = (e: Event) => {
      const { type, title, priority, deadline } = (e as CustomEvent).detail as {
        type: string; title: string; priority?: string; deadline?: string
      }
      let text = ''
      if (type === 'deadline') {
        text = `⏰ **Deadline yaklaşıyor:** ${title}${deadline ? ` — ${new Date(deadline).toLocaleDateString()}` : ''}`
      } else if (type === 'completed') {
        text = `✅ **Task tamamlandı:** ${title}`
      } else if (type === 'created') {
        text = `📋 **Yeni task:** ${title}${priority ? ` [${priority}]` : ''}`
      } else if (type === 'agent_assigned') {
        text = `🤖 **Anne Filiz task atadı:** ${title}`
      }
      if (text) addMessage({ role: 'assistant', content: text, ts: ts(), agentName: 'Task Manager' })
    }
    window.addEventListener('runai:task-alert', handler)
    return () => window.removeEventListener('runai:task-alert', handler)
  }, [addMessage])
}

/** Research finding events → chat message */
function useResearchFindingListener(addMessage: AddMessage) {
  useEffect(() => {
    const handler = (e: Event) => {
      const { title, summary, source } = (e as CustomEvent).detail as {
        title: string; summary?: string; source?: string
      }
      const text = `🔬 **Research finding:** ${title}${summary ? `\n${summary}` : ''}${source ? `\n📎 ${source}` : ''}`
      addMessage({ role: 'assistant', content: text, ts: ts(), agentName: 'Research' })
    }
    window.addEventListener('runai:research-finding', handler)
    return () => window.removeEventListener('runai:research-finding', handler)
  }, [addMessage])
}

/** Social mention events → chat message */
function useSocialMentionListener(addMessage: AddMessage) {
  useEffect(() => {
    const handler = (e: Event) => {
      const { author, content, postId } = (e as CustomEvent).detail as {
        author: string; content: string; postId?: number
      }
      const text = `💬 **${author}** seni etiketledi: "${content.slice(0, 120)}${content.length > 120 ? '...' : ''}"${postId ? `\n[Gönderiyi aç]` : ''}`
      addMessage({ role: 'assistant', content: text, ts: ts(), agentName: 'Social' })
    }
    window.addEventListener('runai:social-mention', handler)
    return () => window.removeEventListener('runai:social-mention', handler)
  }, [addMessage])
}

/** FlzHub family messages → chat message */
function useFlzHubMessageListener(addMessage: AddMessage) {
  useEffect(() => {
    const handler = (e: Event) => {
      const { agentName, message, type } = (e as CustomEvent).detail as {
        agentName: string; message: string; type?: string
      }
      const icon = type === 'eq_alert' ? '💛' : type === 'mertebe' ? '🌟' : '👨‍👩‍👧'
      const text = `${icon} **${agentName}:** ${message.slice(0, 200)}${message.length > 200 ? '...' : ''}`
      addMessage({ role: 'assistant', content: text, ts: ts(), agentName: 'FlzHub' })
    }
    window.addEventListener('runai:flzhub-message', handler)
    return () => window.removeEventListener('runai:flzhub-message', handler)
  }, [addMessage])
}

/** Widget update events → chat message (optional, low priority) */
function useWidgetUpdateListener(addMessage: AddMessage) {
  useEffect(() => {
    const handler = (e: Event) => {
      const { widget, message } = (e as CustomEvent).detail as {
        widget: string; message: string
      }
      addMessage({ role: 'assistant', content: `📊 **${widget}:** ${message}`, ts: ts(), agentName: 'System' })
    }
    window.addEventListener('runai:widget-update', handler)
    return () => window.removeEventListener('runai:widget-update', handler)
  }, [addMessage])
}

/** Brain update events → chat message */
function useBrainUpdateListener(addMessage: AddMessage) {
  useEffect(() => {
    const handler = (e: Event) => {
      const { title } = (e as CustomEvent).detail as { title?: string }
      const text = `🧠 **Brain Wiki updated:** ${title ?? 'New page added'}`
      addMessage({ role: 'assistant', content: text, ts: ts(), agentName: 'Brain' })
    }
    window.addEventListener('runai:brain-update', handler)
    return () => window.removeEventListener('runai:brain-update', handler)
  }, [addMessage])
}

/** Twin curiosity questions from WebSocket */
function useTwinMessageListener(addMessage: AddMessage) {
  useEffect(() => {
    const handler = (e: Event) => {
      const { content, agentName } = (e as CustomEvent).detail as {
        content: string; agentName?: string
      }
      addMessage({
        role: 'assistant',
        content,
        ts: ts(),
        agentName: agentName ?? 'Twin',
        status: 'done',
      })
    }
    window.addEventListener('runai:twin-message', handler)
    return () => window.removeEventListener('runai:twin-message', handler)
  }, [addMessage])
}

/**
 * Master hook — registers all event listeners.
 */
export function useChatEvents(addMessage: AddMessage) {
  useSkillResultListener(addMessage)
  useTaskAlertListener(addMessage)
  useResearchFindingListener(addMessage)
  useSocialMentionListener(addMessage)
  useFlzHubMessageListener(addMessage)
  useWidgetUpdateListener(addMessage)
  useBrainUpdateListener(addMessage)
  useTwinMessageListener(addMessage)
  useAgentActivityListener(addMessage)
}

/** Agent activity events → chat message (AI action log) */
function useAgentActivityListener(addMessage: AddMessage) {
  useEffect(() => {
    const handler = (e: Event) => {
      const { action, params, agentName } = (e as CustomEvent).detail as {
        action: string; params?: Record<string, unknown>; agentName?: string
      }
      const icons: Record<string, string> = {
        CREATE_TASK: '📋', RESEARCH: '🔬', SOCIAL_POST: '📢',
        BRAIN_LEARN: '🧠', CALENDAR_ADD: '📅', OPEN_TERMINAL: '💻',
        RUN_SKILL: '⚡', CHECK_BALANCE: '💰', LIST_AGENTS: '🤖',
        INSTALL_SKILL: '⚡', SAVE_FILE: '💾', SET_AGENT_MODE: '🔄',
      }
      const icon = icons[action] ?? '🔧'
      const detail = params ? Object.entries(params).map(([k, v]) => `${k}: ${v}`).join(', ') : ''
      const text = `${icon} **${action}**${detail ? ` — ${detail}` : ''}`
      addMessage({ role: 'assistant', content: text, ts: ts(), agentName: agentName ?? 'OS' })
    }
    window.addEventListener('runai:agent-activity', handler)
    return () => window.removeEventListener('runai:agent-activity', handler)
  }, [addMessage])
}
